import { useState, useEffect } from "react";
import axios from "axios";
import { Link2, Video, ExternalLink, Loader2, Play, X } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { logEvent } from "@/lib/events";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function getEmbedUrl(url) {
  if (!url) return null;
  // YouTube
  let match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([\w-]+)/);
  if (match) return `https://www.youtube.com/embed/${match[1]}`;
  // Vimeo
  match = url.match(/vimeo\.com\/(\d+)/);
  if (match) return `https://player.vimeo.com/video/${match[1]}`;
  // Already an embed-friendly URL or generic
  return url;
}

export default function LearnerResources() {
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState("all");
  const [embedResource, setEmbedResource] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/learner/resources`, { withCredentials: true })
      .then(({ data }) => setResources(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  // Log event on page view - must be before any early returns
  useEffect(() => { logEvent("resources_page_viewed"); }, []);

  const filtered = resources.filter(
    (r) => filterType === "all" || r.link_type === filterType
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    );
  }

  const handleResourceClick = (r) => {
    logEvent("resource_clicked", { resource_title: r.title, resource_type: r.link_type });
    if (r.link_type === "video" && r.embed) {
      setEmbedResource(r);
    } else {
      window.open(r.url, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <div className="space-y-6" data-testid="learner-resources">
      <div>
        <h1 className="font-heading text-2xl font-bold text-gray-900">Resources</h1>
        <p className="text-sm text-gray-500 font-body mt-1">
          Helpful links and videos for your learning journey.
        </p>
      </div>

      {/* Filter tabs */}
      <div className="flex gap-2">
        {["all", "link", "video"].map((t) => (
          <button
            key={t}
            onClick={() => setFilterType(t)}
            data-testid={`learner-filter-${t}`}
            className={`px-3 py-2 text-sm font-body rounded-lg transition-colors ${
              filterType === t
                ? "text-gray-900 bg-white border border-gray-200 shadow-sm"
                : "text-gray-400 hover:text-gray-900 hover:bg-white/50"
            }`}
          >
            {t === "all" ? "All" : t === "link" ? "Links" : "Videos"}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-16 bg-white border border-gray-200 rounded-xl">
          <Link2 className="h-10 w-10 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-400 font-body">No resources available yet.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((r) => (
            <button
              key={r.id}
              onClick={() => handleResourceClick(r)}
              data-testid={`learner-resource-${r.id}`}
              className="text-left bg-white border border-gray-200 rounded-xl p-5 hover:shadow-md hover:border-gray-300 transition-all duration-200 group"
            >
              <div className="flex items-start justify-between gap-2 mb-3">
                <div
                  className="h-10 w-10 rounded-lg flex items-center justify-center shrink-0"
                  style={{
                    backgroundColor: r.link_type === "video" ? "rgba(239, 68, 68, 0.08)" : "rgba(74, 144, 217, 0.08)",
                  }}
                >
                  {r.link_type === "video" ? (
                    <Video className="h-5 w-5 text-red-500" />
                  ) : (
                    <Link2 className="h-5 w-5" style={{ color: "var(--theme-primary, var(--primary-color, #4A90D9))" }} />
                  )}
                </div>
                {r.link_type === "video" && r.embed ? (
                  <Play className="h-4 w-4 text-gray-300 group-hover:text-gray-500 shrink-0" />
                ) : (
                  <ExternalLink className="h-4 w-4 text-gray-300 group-hover:text-gray-500 shrink-0" />
                )}
              </div>
              <h3 className="text-sm font-body font-semibold text-gray-900 mb-1 line-clamp-2">{r.title}</h3>
              <p className="text-xs text-gray-400 font-body truncate">{r.url}</p>
              <div className="mt-3 flex items-center gap-1.5">
                <Badge
                  variant="outline"
                  className={`text-xs font-body ${
                    r.link_type === "video"
                      ? "border-red-200 text-red-500"
                      : "border-gray-200 text-gray-500"
                  }`}
                >
                  {r.link_type === "video" ? "Video" : "Link"}
                </Badge>
                {r.link_type === "video" && r.embed && (
                  <Badge className="text-xs font-body bg-purple-50 text-purple-500 border-purple-200">
                    Inline
                  </Badge>
                )}
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Embed Video Modal */}
      <Dialog open={!!embedResource} onOpenChange={() => setEmbedResource(null)}>
        <DialogContent
          className="bg-white border-gray-200 text-gray-900 max-w-4xl p-0 overflow-hidden"
          data-testid="video-embed-modal"
        >
          <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100">
            <h3 className="font-body font-bold text-base text-gray-900 truncate pr-4">
              {embedResource?.title}
            </h3>
            <button
              onClick={() => setEmbedResource(null)}
              className="text-gray-400 hover:text-gray-900 p-1 rounded-lg hover:bg-gray-100 transition-colors shrink-0"
              data-testid="video-embed-close"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          <div className="relative w-full" style={{ paddingBottom: "56.25%" }}>
            {embedResource && (
              <iframe
                src={getEmbedUrl(embedResource.url)}
                className="absolute inset-0 w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                title={embedResource.title}
                data-testid="video-embed-iframe"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
